// src/components/EnterpriseRequest.js
import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import inquiryService from '../services/inquiryService';
import { getCurrentUserId } from '../services/database';

const EnterpriseRequest = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    category: '',
    details: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [dirtyFields, setDirtyFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const MIN_CHARS = 25;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirtyFields(prev => ({ ...prev, [field]: true }));
    
    // Clear confirmation when user starts editing again
    if (showConfirmation) {
      setShowConfirmation(false);
    }
  };

  const handleBlur = (field) => {
    setDirtyFields(prev => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const validateField = (field) => {
    if (!dirtyFields[field]) return true;
    
    const newErrors = { ...errors };
    
    switch (field) {
      case 'category':
        if (!formData.category) {
          newErrors.category = 'Please select a request category.';
        } else {
          delete newErrors.category;
        }
        break;
      case 'details':
        const val = formData.details.trim();
        if (!val) {
          newErrors.details = 'This field is required.';
        } else if (val.length < MIN_CHARS) {
          newErrors.details = `Please enter at least ${MIN_CHARS} characters.`;
        } else {
          delete newErrors.details;
        }
        break;
      case 'email':
        const emailVal = formData.email.trim();
        if (!emailVal) {
          newErrors.email = 'Email is required.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
          newErrors.email = 'Please enter a valid email address.';
        } else {
          delete newErrors.email;
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return !newErrors[field];
  };

  const isFormValid = () => {
    const validCat = formData.category !== '';
    const validDet = formData.details.trim().length >= MIN_CHARS;
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
    return validCat && validDet && validEmail;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as dirty to show all errors
    const allFieldsDirty = {
      category: true,
      details: true,
      email: true
    };
    setDirtyFields(allFieldsDirty);

    // Validate all fields
    let allValid = true;
    Object.keys(allFieldsDirty).forEach(field => {
      if (!validateField(field)) {
        allValid = false;
      }
    });

    if (!allValid) return;

    setIsSubmitting(true);
    
    try {
      // Get current user ID for authenticated requests
      let userId = null;
      try {
        userId = await getCurrentUserId();
      } catch (error) {
        console.log('User not authenticated, proceeding as guest inquiry');
      }

      // Prepare inquiry data with enterprise request flag
      const inquiryData = {
        name: 'Enterprise Admin Request', // Generic name for enterprise requests
        email: formData.email.trim(),
        subject: 'enterprise-admin-request', // Special subject to identify enterprise requests
        message: `Category: ${formData.category}\n\nDetails: ${formData.details.trim()}`,
        authenticated: !!userId,
        userId: userId,
        isEnterpriseRequest: true // Special flag to identify enterprise requests
      };

      await inquiryService.submitInquiry(inquiryData);
      
      // Reset form and show confirmation
      setFormData({ category: '', details: '', email: '' });
      setDirtyFields({});
      setErrors({});
      setShowConfirmation(true);
      
    } catch (error) {
      console.error('Error submitting enterprise request:', error);
      // You might want to show an error message to the user here
      alert('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ category: '', details: '', email: '' });
    setDirtyFields({});
    setErrors({});
    setShowConfirmation(false);
    onClose(); // Close the modal
  };

  const handleClose = () => {
    handleCancel();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Enterprise Request</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Confirmation Message */}
          {showConfirmation && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-5 flex items-start gap-3 mb-6">
              <CheckCircle className="text-green-600 w-6 h-6 mt-1 flex-shrink-0" />
              <div>
                <div className="font-semibold text-green-700 mb-1">Your request was received.</div>
                <div className="text-green-700 text-sm">
                  We will respond within 2 business days. If you do not hear from us within 2 business days, 
                  please check your spam folder for emails coming from dessino.ai.
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <p className="text-gray-700 mb-4">
                You must make a request to change administrator and delete data for a specific enterprise user. 
                Please enter the details of your request below.
              </p>
            </div>

            {/* Category Selection */}
            <div>
              <label htmlFor="category" className="block text-gray-900 font-medium mb-1">
                Choose request category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                onBlur={() => handleBlur('category')}
                className="w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-gray-900 bg-white py-2 px-3"
              >
                <option value="">Select an option</option>
                <option value="change-admin">Change administrator</option>
                <option value="change-enterprise-name">Change enterprise name</option>
                <option value="delete-user-data">Delete user data</option>
                <option value="renew-subscription">Inquire about your subscription</option>
                <option value="other">Other requests</option>
              </select>
              {errors.category && (
                <div className="text-red-600 text-sm mt-1">{errors.category}</div>
              )}
            </div>

            {/* Details */}
            <div>
              <label htmlFor="details" className="block text-gray-900 font-medium mb-1">
                Please describe your request. <span className="text-red-500">*</span>
              </label>
              <textarea
                id="details"
                value={formData.details}
                onChange={(e) => handleInputChange('details', e.target.value)}
                onBlur={() => handleBlur('details')}
                rows="5"
                placeholder="Enter details concerning your request"
                className="w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-gray-900 bg-white py-2 px-3 resize-vertical"
              />
              <div className="text-gray-500 text-sm mt-1 text-right">
                <span>{formData.details.trim().length}</span>/{MIN_CHARS} characters
              </div>
              {errors.details && (
                <div className="text-red-600 text-sm mt-1">{errors.details}</div>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-gray-900 font-medium mb-1">
                Email address to reach you. <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                placeholder="you@company.com"
                className="w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition text-gray-900 bg-white py-2 px-3"
              />
              {errors.email && (
                <div className="text-red-600 text-sm mt-1">{errors.email}</div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleCancel}
                className="min-w-[120px] px-5 py-2 rounded-md border border-gray-400 text-gray-900 bg-white hover:bg-gray-50 transition font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting}
                className="min-w-[140px] px-5 py-2 rounded-md bg-gray-900 text-white font-semibold hover:bg-gray-800 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseRequest;